// src/components/resources/templates/TemplateViewerWrapper.tsx

'use client'

import { useState, useCallback, useMemo, type ReactNode } from 'react'
import Image from 'next/image'
import Link from 'next/link'
import { useIsomorphicLayoutEffect } from '~/lib/hooks/useIsomorphicLayoutEffect'
import { type Template, type TemplateDeliveryFormatId, TemplateDeliveryFormats } from '~/lib/resources/types/templates'
import type { RelatedResource } from '~/lib/resources/types/shared/base'
import { TemplateViewer } from '~/components/resources/templates/TemplateViewer'
import { FormatSelector } from '~/components/resources/templates/FormatSelector'
import { FAQ } from '~/components/shared/FAQ'
import {
  ArrowDownTrayIcon,
  ShareIcon,
  ClockIcon,
  ChartBarIcon,
  CheckCircleIcon,
  DocumentTextIcon,
  ArrowRightIcon,
  SparklesIcon,
} from '@heroicons/react/24/outline'

interface TemplateViewerWrapperProps {
  template: Template
}

/**
 * Render a free-text content block as structured HTML:
 *  - a numbered procedure ("1. … 2. …") → an ordered list (intro lines kept as paragraphs)
 *  - other multi-line copy → a bulleted list
 *  - a single line → a paragraph
 * Keeps the authored data simple (newline-joined strings) while giving the
 * page real typographic structure instead of a pre-wrapped wall of text.
 */
function FormattedContent({ text }: { text: string }): JSX.Element {
  const lines = text.split('\n').map((l) => l.trim()).filter(Boolean)
  const isStep = (l: string): boolean => /^\d+\.\s/.test(l)
  const numbered = lines.filter(isStep)

  if (numbered.length >= 2) {
    const intro = lines.filter((l) => !isStep(l))
    return (
      <div className="space-y-4">
        {intro.map((p, i) => (
          <p key={`p-${i}`} className="text-gray-600 dark:text-gray-300">{p}</p>
        ))}
        <ol className="list-decimal space-y-2.5 pl-5 text-gray-600 marker:font-semibold marker:text-gray-400 dark:text-gray-300">
          {numbered.map((l, i) => (
            <li key={`s-${i}`} className="pl-1.5 leading-relaxed">{l.replace(/^\d+\.\s/, '')}</li>
          ))}
        </ol>
      </div>
    )
  }

  if (lines.length > 1) {
    return (
      <ul className="list-disc space-y-2 pl-5 text-gray-600 marker:text-gray-400 dark:text-gray-300">
        {lines.map((l, i) => (
          <li key={i} className="pl-1.5 leading-relaxed">{l.replace(/^[-•]\s*/, '')}</li>
        ))}
      </ul>
    )
  }

  return <p className="text-gray-600 dark:text-gray-300">{lines[0] ?? ''}</p>
}

function Section({ title, children }: { title: string; children: ReactNode }): JSX.Element {
  return (
    <section className="space-y-4">
      <h2 className="text-xl font-bold tracking-tight text-gray-900 dark:text-white">{title}</h2>
      {children}
    </section>
  )
}

function MetaChip({ icon: Icon, children }: { icon: typeof ClockIcon; children: ReactNode }): JSX.Element {
  return (
    <span className="inline-flex items-center gap-1.5 rounded-full border border-gray-200 bg-white px-3 py-1 text-sm font-medium text-gray-700 dark:border-gray-700 dark:bg-gray-900 dark:text-gray-200">
      <Icon className="h-4 w-4 text-gray-400" aria-hidden="true" />
      {children}
    </span>
  )
}

export function TemplateViewerWrapper({ template }: TemplateViewerWrapperProps): JSX.Element {
  const formats = template.config.source.formats
  const [activeFormat, setActiveFormat] = useState<TemplateDeliveryFormatId>(formats[0]?.formatId ?? 'pdf')
  const [showEmailCapture, setShowEmailCapture] = useState(false)
  const [accessGranted, setAccessGranted] = useState(!template.config.access?.hasGatedFormats)
  const [copied, setCopied] = useState(false)

  // Cache Components keeps this route mounted (Activity) when hidden, so
  // `showEmailCapture` would persist and the gated email-capture modal would
  // re-open stale over the template page when the user navigates back. Close it
  // when the route is hidden (and on unmount). Mirrors the ebook/quiz/persona
  // transient capture-dialog resets in this migration.
  useIsomorphicLayoutEffect(() => {
    return () => setShowEmailCapture(false)
  }, [])

  const isGated = Boolean(template.config.access?.hasGatedFormats)
  const heroImage = template.images?.og ?? template.images?.hero ?? template.images?.thumbnail
  const formatNames = useMemo(
    () =>
      formats
        .map((f) => Object.values(TemplateDeliveryFormats).find((d) => d.id === f.formatId)?.name ?? f.formatId)
        .join(' · '),
    [formats],
  )
  const relatedLinks = useMemo(
    () =>
      (template.relatedResources ?? []).filter(
        (r): r is RelatedResource => typeof r !== 'string' && Boolean(r.fullRoute),
      ),
    [template.relatedResources],
  )
  const difficultyLabel = template.difficulty
    ? template.difficulty.charAt(0).toUpperCase() + template.difficulty.slice(1)
    : null

  // An interactive companion tool (e.g. the Marketing Budget Calculator for the
  // budget template) gets surfaced as a prominent callout so the "interactive
  // version" is easy to find from the template page.
  const interactiveTool = useMemo(
    () =>
      relatedLinks.find(
        (r) => r.resourceType === 'tool' && (r.toolType === 'calculator' || r.toolType === 'generator'),
      ),
    [relatedLinks],
  )

  const handleDownload = useCallback(() => {
    const cfg = formats.find((f) => f.formatId === activeFormat)
    if (!cfg) return
    if (cfg.isGated && !accessGranted) setShowEmailCapture(true)
    else window.open(cfg.contentUrl, '_blank')
  }, [formats, activeFormat, accessGranted])

  const handleShare = useCallback(async () => {
    try {
      await navigator.share({ title: template.title, url: window.location.href })
    } catch {
      try {
        await navigator.clipboard.writeText(window.location.href)
        setCopied(true)
        setTimeout(() => setCopied(false), 2000)
      } catch {
        /* no-op */
      }
    }
  }, [template.title])

  const ctaLabel = isGated && !accessGranted ? 'Email me the template' : 'Download template'

  return (
    <div className="mx-auto max-w-5xl px-4 pb-20 sm:px-6 lg:px-8">
      {/* Hero */}
      <header className="space-y-5 pt-2">
        <h1 className="text-3xl font-bold tracking-tight text-gray-900 dark:text-white sm:text-4xl">
          {template.title}
        </h1>
        <p className="max-w-3xl text-lg text-gray-600 dark:text-gray-300">{template.description}</p>
        <div className="flex flex-wrap items-center gap-2.5">
          {template.estimatedTime && <MetaChip icon={ClockIcon}>{template.estimatedTime}</MetaChip>}
          {difficultyLabel && <MetaChip icon={ChartBarIcon}>{difficultyLabel}</MetaChip>}
          <MetaChip icon={DocumentTextIcon}>{formatNames}</MetaChip>
        </div>
        <div className="flex flex-wrap gap-3 pt-1">
          <button
            onClick={handleDownload}
            className="inline-flex items-center justify-center gap-2 rounded-lg bg-blue-600 px-5 py-2.5 text-sm font-semibold text-white shadow-sm transition-colors hover:bg-blue-500 focus-visible:outline focus-visible:outline-2 focus-visible:outline-offset-2 focus-visible:outline-blue-600 dark:bg-blue-500 dark:hover:bg-blue-400"
          >
            <ArrowDownTrayIcon className="h-5 w-5" /> {ctaLabel}
          </button>
          <button
            onClick={handleShare}
            className="inline-flex items-center justify-center gap-2 rounded-lg bg-gray-100 px-5 py-2.5 text-sm font-semibold text-gray-900 transition-colors hover:bg-gray-200 dark:bg-gray-800 dark:text-gray-100 dark:hover:bg-gray-700"
          >
            <ShareIcon className="h-5 w-5" /> {copied ? 'Link copied!' : 'Share'}
          </button>
        </div>
      </header>

      {/* Hero image */}
      {heroImage && (
        <div className="mt-8 overflow-hidden rounded-2xl border border-gray-200 shadow-sm dark:border-gray-800">
          <Image
            src={heroImage.url}
            alt={heroImage.alt ?? `${template.title} preview`}
            width={heroImage.width ?? 1555}
            height={heroImage.height ?? 816}
            className="h-auto w-full"
            priority
          />
        </div>
      )}

      {/* Live embed (Canva/Figma/Google) when the format supports it + email modal */}
      <div className="mt-8 empty:mt-0">
        <TemplateViewer
          template={template}
          activeFormat={activeFormat}
          showEmailCapture={showEmailCapture}
          onCloseEmailCapture={() => setShowEmailCapture(false)}
          accessGranted={accessGranted}
          onEmailSuccess={() => {
            setAccessGranted(true)
            setShowEmailCapture(false)
          }}
        />
      </div>

      {/* Interactive companion callout */}
      {interactiveTool && (
        <Link
          href={interactiveTool.fullRoute ?? '#'}
          className="mt-8 flex items-center justify-between gap-4 rounded-2xl border border-blue-200 bg-blue-50 p-5 transition-colors hover:bg-blue-100 dark:border-blue-900/50 dark:bg-blue-900/20 dark:hover:bg-blue-900/30"
        >
          <span className="flex items-center gap-3">
            <span className="flex h-10 w-10 flex-shrink-0 items-center justify-center rounded-lg bg-blue-600 text-white">
              <SparklesIcon className="h-5 w-5" aria-hidden="true" />
            </span>
            <span>
              <span className="block font-semibold text-gray-900 dark:text-white">
                Prefer to build it interactively?
              </span>
              <span className="block text-sm text-gray-600 dark:text-gray-300">
                Try the {interactiveTool.title} — allocate live, then export.
              </span>
            </span>
          </span>
          <ArrowRightIcon className="h-5 w-5 flex-shrink-0 text-blue-600 dark:text-blue-400" aria-hidden="true" />
        </Link>
      )}

      {/* Body */}
      <div className="mt-10 grid gap-10 lg:grid-cols-[minmax(0,1fr),300px]">
        <article className="min-w-0 space-y-10">
          {template.sections.map((section) => (
            <Section key={section.id} title={section.title}>
              <FormattedContent text={section.content} />
            </Section>
          ))}

          {template.config.usage?.instructions && (
            <Section title="How to use this template">
              <FormattedContent text={template.config.usage.instructions} />
            </Section>
          )}

          {template.config.usage?.tips && template.config.usage.tips.length > 0 && (
            <Section title="Pro tips">
              <ul className="space-y-3">
                {template.config.usage.tips.map((tip, i) => (
                  <li key={i} className="flex gap-3">
                    <CheckCircleIcon className="mt-0.5 h-5 w-5 flex-shrink-0 text-blue-500" aria-hidden="true" />
                    <span className="text-gray-600 dark:text-gray-300">
                      <span className="font-semibold text-gray-900 dark:text-white">{tip.title}:</span> {tip.content}
                    </span>
                  </li>
                ))}
              </ul>
            </Section>
          )}

          {template.config.usage?.examples && template.config.usage.examples.length > 0 && (
            <Section title="Example uses">
              <div className="grid gap-4 sm:grid-cols-2">
                {template.config.usage.examples.map((ex, i) => (
                  <div key={i} className="rounded-xl border border-gray-200 bg-white p-5 dark:border-gray-800 dark:bg-gray-900">
                    <h3 className="font-semibold text-gray-900 dark:text-white">{ex.title}</h3>
                    <p className="mt-1.5 text-sm leading-relaxed text-gray-600 dark:text-gray-400">{ex.description}</p>
                  </div>
                ))}
              </div>
            </Section>
          )}

          {template.config.platforms && template.config.platforms.length > 0 && (
            <Section title="Works with">
              <div className="flex flex-wrap gap-2">
                {template.config.platforms.map((p) => (
                  <span
                    key={p.name}
                    title={p.notes}
                    className="inline-flex items-center rounded-lg border border-gray-200 bg-white px-3 py-1.5 text-sm font-medium text-gray-700 dark:border-gray-700 dark:bg-gray-900 dark:text-gray-200"
                  >
                    {p.name}
                  </span>
                ))}
              </div>
            </Section>
          )}

          {template.config.faqs && template.config.faqs.length > 0 && (
            <Section title="Frequently asked questions">
              {/* Emit FAQPage JSON-LD here: template FAQs live under
                  config.faqs, which the resource structured-data provider
                  (getResourceFaqs → top-level resource.faqs only) doesn't see,
                  so this is the page's only FAQPage source. */}
              <FAQ
                type="single"
                features={{ showTitle: false }}
                items={template.config.faqs.map((f) => ({ question: f.question, answer: f.answer }))}
              />
            </Section>
          )}
        </article>

        {/* Sidebar */}
        <aside className="lg:sticky lg:top-24 lg:self-start">
          <div className="space-y-6">
            <div className="rounded-2xl border border-gray-200 bg-white p-6 dark:border-gray-800 dark:bg-gray-900">
              <h3 className="text-base font-semibold text-gray-900 dark:text-white">Get this template</h3>
              {formats.length > 1 && (
                <div className="mt-4">
                  <FormatSelector
                    formats={formats.map((f) => f.formatId)}
                    activeFormat={activeFormat}
                    onFormatSelect={setActiveFormat}
                  />
                </div>
              )}
              <button
                onClick={handleDownload}
                className="mt-4 inline-flex w-full items-center justify-center gap-2 rounded-lg bg-blue-600 px-4 py-2.5 text-sm font-semibold text-white shadow-sm transition-colors hover:bg-blue-500 dark:bg-blue-500 dark:hover:bg-blue-400"
              >
                <ArrowDownTrayIcon className="h-5 w-5" /> {ctaLabel}
              </button>
              <p className="mt-2 text-center text-xs text-gray-500 dark:text-gray-400">
                {isGated ? 'Free — we’ll email you the link.' : 'Free download.'} {formatNames}.
              </p>

              {template.config.usage?.prerequisites && template.config.usage.prerequisites.length > 0 && (
                <div className="mt-6 border-t border-gray-200 pt-5 dark:border-gray-800">
                  <h4 className="mb-3 text-sm font-semibold text-gray-900 dark:text-white">Before you start</h4>
                  <ul className="space-y-2.5">
                    {template.config.usage.prerequisites.map((p, i) => (
                      <li key={i} className="flex gap-2.5 text-sm text-gray-600 dark:text-gray-400">
                        <CheckCircleIcon className="mt-0.5 h-4 w-4 flex-shrink-0 text-green-500" aria-hidden="true" />
                        <span>{p}</span>
                      </li>
                    ))}
                  </ul>
                </div>
              )}
            </div>

            {relatedLinks.length > 0 && (
              <div className="rounded-2xl border border-gray-200 bg-white p-6 dark:border-gray-800 dark:bg-gray-900">
                <h3 className="text-base font-semibold text-gray-900 dark:text-white">Related resources</h3>
                <ul className="mt-4 space-y-3">
                  {relatedLinks.slice(0, 5).map((ref) => (
                    <li key={ref.fullRoute ?? ref.id}>
                      <Link
                        href={ref.fullRoute ?? '#'}
                        className="group flex items-start gap-2 text-sm font-medium text-blue-600 hover:underline dark:text-blue-400"
                      >
                        <ArrowRightIcon className="mt-0.5 h-4 w-4 flex-shrink-0 opacity-60 transition-transform group-hover:translate-x-0.5" aria-hidden="true" />
                        <span>{ref.title}</span>
                      </Link>
                    </li>
                  ))}
                </ul>
              </div>
            )}
          </div>
        </aside>
      </div>
    </div>
  )
}
